import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import dayjs from 'dayjs';
import { message } from 'antd';

import i18n from 'src/i18n';
import { SOURCE_CATEGORY_TC, SOURCE_CATEGORY_TYPE } from 'src/constants/normal';
import { formatDayjsWithWeekday } from 'src/utils';

import EDMService from 'src/services/edm';
import ResultService from 'src/services/result';
import ProjectService from 'src/services/project';
import mainStore from 'src/stores/mainStore';

// > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
// ? 因為這邊 paging 的方式是固定 頁數 * 每頁筆數, 所以：
// ? 如果第一次撈完去重之後，剩下的筆數是 37, 要再去撈下 50筆
// ? 等到後面 50 筆也處理完之後，假設現在有 83 筆，一樣只顯示 50 筆
// ? 但把多的 33 筆保留在 remainingTopicList 裡面。
// ? 撈下一頁的時候，不用考慮這些目前沒有用到的，繼續往後面撈，然後再處理。
// ? 直到下一次超過50筆時，再把 新的 50 推到 topicList 裡面。
// ? 剩下的一樣再推回 remainingTopicList 裡面。
// ! 就可以避免因為志維的 anchor 是沒有 id 的關係，導致在撈下一頁的時候，會撈到重複的話題。
export default class TopicPreviewPageViewModel {
  projectId = null;
  newsletterId = null;
  @observable newsletterSubject = '主題電子報主旨';

  resultId = null;
  @observable startDate = '';
  @observable endDate = '';
  subjectId = null;
  @observable subjectName = '主題話題';
  @observable subjectHasEvaluation = false;
  @observable subjectNeedShowBrand = false;

  @observable topicList = [];
  // > 新增剩下的 topics list 來保留超過 limit 的筆數, 下次再撈下一頁的時候, 可以繼續使用這些目前沒有在畫面上的。
  @observable remainingTopicList = [];
  searchId = null;
  @observable hasNextPage = true;

  // > 用來過濾重複話題的
  @observable urlSet = new Set();

  @observable tagPoolMap = new Map();

  @observable currentPage = 1;

  @computed get dateString() {
    if (dayjs(this.endDate).diff(this.startDate, 'day') > 1) {
      return `${formatDayjsWithWeekday(this.startDate)} - ${formatDayjsWithWeekday(this.endDate)}`;
    }
    return formatDayjsWithWeekday(this.startDate);
  }

  constructor() {
    makeObservable(this);
  }

  @action didMount = async ({ id, rid, sid }) => {
    if (this.newsletterId === id) {
      return;
    }

    this.newsletterId = id;
    this.resultId = rid;
    this.subjectId = sid;


    await Promise.all([
      this.getNewsletterDetail(),
      this.getResultDetail()
    ]);

    await Promise.all([
      this.processTopics()
    ]);
  };

  @action getNewsletterDetail = async () => {
    try {
      if (mainStore.checkIsInQueue('newsletterDetail')) {
        return;
      }
      mainStore.setLoading('newsletterDetail');
      const { projectId, subject, tags } = await EDMService.getEDMDetail(this.newsletterId);
      const { topics } = await ResultService.getResultDetail(this.newsletterId, this.resultId);

      runInAction(() => {
        this.projectId = projectId;
        this.newsletterSubject = subject;
        this.tagPoolMap = new Map(tags.map((tag) => [tag.id, tag.value]));
      });
      const targetSubject = topics.find((s) => s.id === this.subjectId);
      const { name, hasEvaluation, subjectNeedShowBrand } = await this.updateSubjectContent(targetSubject);
      runInAction(() => {
        this.subjectName = name;
        this.subjectHasEvaluation = hasEvaluation;
        this.subjectNeedShowBrand = subjectNeedShowBrand;
      });

    } catch (error) {
      message.error(i18n.t('api_get_edm_detail_error'));
    } finally {
      mainStore.setLoadingComplete('newsletterDetail');
    }
  };

  @action getResultDetail = async () => {
    try {
      if (mainStore.checkIsInQueue('resultDetail')) {
        return;
      }
      mainStore.setLoading('resultDetail');
      const { startDate, endDate } = await ResultService.getResultDetail(this.newsletterId, this.resultId);
      runInAction(() => {
        this.startDate = startDate;
        this.endDate = endDate;
      });

    } catch (error) {
      message.error(i18n.t('api_get_edm_result_detail_error'));
    } finally {
      mainStore.setLoadingComplete('resultDetail');
    }
  };

  @action updateSubjectContent = async (subjectItem) => {
    const {
      hasEvaluation,
      searchKeywordLevel1Id,
      searchKeywordLevel2Id,
      searchKeywordLevel3Id,
      searchTagParentId,
      searchTagId,
      category
    } = subjectItem;

    const sourceText = !category[0]
      ? ''
      : `-${SOURCE_CATEGORY_TC[category[0]]}`;


    let name = '';

    if (searchKeywordLevel1Id && searchKeywordLevel2Id) {
      const [brand, productLine] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id, searchKeywordLevel2Id]);
      name = `${brand.name}/${productLine.name}${sourceText}`;
    }

    if (searchKeywordLevel1Id && searchKeywordLevel3Id) {
      const [brand, product] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id, searchKeywordLevel3Id]);
      name = `${brand.name}/${product.name}${sourceText}`;
    }

    if (searchKeywordLevel1Id && !searchKeywordLevel2Id && !searchKeywordLevel3Id) {
      const [brand] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id]);
      name = `${brand.name}${sourceText}`;
    }

    if (searchTagParentId) {
      const [tagParent, tag] = await ProjectService.getTagContentByIds(this.projectId, [searchTagParentId, searchTagId]);
      if (searchKeywordLevel1Id) {
        const [brand] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id]);
        name = `${brand.name}/${tagParent.name}/${tag.name}${sourceText}`;
      } else {
        name = `${tagParent.name}/${tag.name}${sourceText}`;
      }
    }

    return {
      name,
      hasEvaluation,
      subjectNeedShowBrand: searchTagParentId && !searchKeywordLevel1Id
    };

  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action processTopics = async () => {
    if (this.remainingTopicList.length > 20) {
      this.topicList = [...this.topicList, ...this.remainingTopicList.slice(0, 20)];
      this.remainingTopicList = this.remainingTopicList.slice(20);
      return;
    }

    if (this.hasNextPage) {
      try {
        if (this.currentPage === 1) {
          await this.fetchPosts();
        } else {
          await this.pagingPosts();
        }
      } catch (error) {
        message.error(i18n.t('api_get_topics_error'));
      }
    }

    if (this.remainingTopicList.length < 20 && this.hasNextPage) {
      await this.processTopics();
    } else {
      runInAction(() => {
        this.topicList = [...this.topicList, ...this.remainingTopicList.slice(0, 20)];
        this.remainingTopicList = this.remainingTopicList.slice(20);
      });
    }

  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action fetchPosts = async () => {
    try {
      if (mainStore.checkIsInQueue('topics')) {
        return;
      }
      mainStore.setLoading('topics');
      const { list, searchId } = await ResultService.getResultTopics(
        this.newsletterId,
        {
          date: {
            gte: this.startDate,
            lte: this.endDate
          },
          subjectId: this.subjectId,
          excludeNotSend: true
        }
      );

      // > 因為要過濾話題 size 改成先撈取 50 筆
      runInAction(() => {
        this.searchId = searchId;
        this.hasNextPage = list.length === 50;
        this.currentPage += 1;
      });

      this.filterPosts(list);
    } catch (error) {
      message.error(i18n.t('api_get_topics_error'));
      this.hasNextPage = false;
    } finally {
      mainStore.setLoadingComplete('topics');
    }
  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action pagingPosts = async () => {
    try {
      if (mainStore.checkIsInQueue('topics')) {
        return;
      }
      mainStore.setLoading('topics');
      // > 因為要過濾話題 size 改成先撈取 50 筆
      const { list } = await ResultService.getResultTopicsByPage(this.newsletterId, { page: this.currentPage, searchId: this.searchId, size: 50 });

      // > 因為要過濾話題 size 改成先撈取 50 筆
      runInAction(() => {
        this.hasNextPage = list.length === 50;
        this.currentPage += 1;
      });

      this.filterPosts(list);

    } catch (error) {
      message.error(i18n.t('api_get_topics_error'));
      this.hasNextPage = false;
    } finally {
      mainStore.setLoadingComplete('topics');
    }
  };

  @action filterPosts = (posts) => {
    posts.forEach((post) => {
      if ([SOURCE_CATEGORY_TYPE.News, SOURCE_CATEGORY_TYPE.Forum, SOURCE_CATEGORY_TYPE.Threads, SOURCE_CATEGORY_TYPE.IG].includes(post.category)) {
        const url = post.url;
        if (this.urlSet.has(url)) {
          return;
        }
        this.urlSet.add(url);
        this.remainingTopicList.push(post);
      } else {
        this.remainingTopicList.push(post);
      }
    });
  };
}
